/**
 * Game state: the player's grid, their pencil marks, and the move history.
 *
 * Everything the player does becomes a `Move` holding the before and after
 * state of every cell it touched. Undo replays `before`, redo replays `after`.
 * That is more bookkeeping than storing single-cell edits, but it is the only
 * shape that makes batch changes -- erasing a cell, or auto-cleaning a digit
 * out of twenty peers' notes -- undo in one step like the player expects.
 *
 * Undo is unlimited. The study lists losing progress among the loudest
 * complaints about the incumbents, and a bounded history is a small version of
 * the same sin.
 */

import {
  CELLS,
  EMPTY,
  boardFromString,
  boardToString,
  cloneBoard,
  type Board,
  type Digit,
} from "../core/grid";
import { CLASSIC, type ConstraintSet } from "../core/constraints";
import { computeCandidates, conflictingCells, maskOf } from "../core/candidates";
import type { Tier } from "../core/techniques/types";
import type { TechniqueId } from "../core/techniques/types";

export type MarkKind = "corner" | "center";

interface CellSnapshot {
  readonly cell: number;
  readonly value: number;
  readonly corner: number;
  readonly center: number;
}

interface Move {
  readonly before: readonly CellSnapshot[];
  readonly after: readonly CellSnapshot[];
}

export interface PuzzleMeta {
  readonly tier: Tier | "unrated";
  readonly techniques: readonly TechniqueId[];
  readonly seed: number;
}

/** Everything needed to restore a game, in a form that survives JSON. */
export interface GameSnapshot {
  readonly puzzle: string;
  readonly solution: string;
  readonly values: string;
  readonly corner: readonly number[];
  readonly center: readonly number[];
  readonly elapsedMs: number;
  readonly meta: PuzzleMeta;
  readonly createdAt: number;
}

export class Game {
  readonly puzzle: Board;
  readonly solution: Board;
  readonly meta: PuzzleMeta;
  readonly createdAt: number;

  private readonly cs: ConstraintSet;
  private readonly values: Uint8Array;
  private readonly corner: Int16Array;
  private readonly center: Int16Array;
  private readonly undoStack: Move[] = [];
  private readonly redoStack: Move[] = [];

  /** Milliseconds banked before the current run of the clock. */
  private banked = 0;
  private runningSince: number | null = null;

  private constructor(
    puzzle: Board,
    solution: Board,
    meta: PuzzleMeta,
    cs: ConstraintSet,
    createdAt: number,
  ) {
    this.puzzle = puzzle;
    this.solution = solution;
    this.meta = meta;
    this.cs = cs;
    this.createdAt = createdAt;
    this.values = cloneBoard(puzzle);
    this.corner = new Int16Array(CELLS);
    this.center = new Int16Array(CELLS);
  }

  static create(
    puzzle: Board,
    solution: Board,
    meta: PuzzleMeta,
    options: { cs?: ConstraintSet; now?: number } = {},
  ): Game {
    return new Game(
      cloneBoard(puzzle),
      cloneBoard(solution),
      meta,
      options.cs ?? CLASSIC,
      options.now ?? Date.now(),
    );
  }

  static restore(snapshot: GameSnapshot, cs: ConstraintSet = CLASSIC): Game {
    const game = new Game(
      boardFromString(snapshot.puzzle),
      boardFromString(snapshot.solution),
      snapshot.meta,
      cs,
      snapshot.createdAt,
    );
    game.values.set(boardFromString(snapshot.values));
    game.corner.set(snapshot.corner);
    game.center.set(snapshot.center);
    game.banked = snapshot.elapsedMs;
    return game;
  }

  toSnapshot(): GameSnapshot {
    return {
      puzzle: boardToString(this.puzzle),
      solution: boardToString(this.solution),
      values: boardToString(this.values),
      corner: [...this.corner],
      center: [...this.center],
      elapsedMs: this.elapsedMs(),
      meta: this.meta,
      createdAt: this.createdAt,
    };
  }

  // ---------- reading ----------

  isGiven(cell: number): boolean {
    return this.puzzle[cell] !== EMPTY;
  }

  valueAt(cell: number): number {
    return this.values[cell]!;
  }

  marksAt(cell: number, kind: MarkKind): number {
    return kind === "corner" ? this.corner[cell]! : this.center[cell]!;
  }

  /** The board as the solvers want it -- givens plus whatever the player typed. */
  board(): Board {
    return cloneBoard(this.values);
  }

  isComplete(): boolean {
    for (let cell = 0; cell < CELLS; cell++) if (this.values[cell] === EMPTY) return false;
    return true;
  }

  /** Complete and correct. The only state that counts as a win. */
  isSolved(): boolean {
    for (let cell = 0; cell < CELLS; cell++) {
      if (this.values[cell] !== this.solution[cell]) return false;
    }
    return true;
  }

  /** Cells duplicating a digit within a unit. Always shown -- it is not a spoiler. */
  conflicts(): Set<number> {
    return conflictingCells(this.values, this.cs);
  }

  /**
   * Cells holding a digit the solution disagrees with. Only surfaced when the
   * player has opted into mistake checking -- a purist run must be able to go
   * wrong in peace.
   */
  mistakes(): Set<number> {
    const wrong = new Set<number>();
    for (let cell = 0; cell < CELLS; cell++) {
      if (this.values[cell] !== EMPTY && this.values[cell] !== this.solution[cell]) {
        wrong.add(cell);
      }
    }
    return wrong;
  }

  /** How many of `digit` are still to be placed; 0 means the digit is done. */
  remaining(digit: Digit): number {
    let placed = 0;
    for (let cell = 0; cell < CELLS; cell++) if (this.values[cell] === digit) placed++;
    return 9 - placed;
  }

  /** Candidates implied by the current grid, for the auto-candidates display. */
  autoCandidates(): Int16Array {
    return computeCandidates(this.values, this.cs);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  // ---------- writing ----------

  private snapshotOf(cell: number): CellSnapshot {
    return {
      cell,
      value: this.values[cell]!,
      corner: this.corner[cell]!,
      center: this.center[cell]!,
    };
  }

  private applySnapshot(state: CellSnapshot): void {
    this.values[state.cell] = state.value;
    this.corner[state.cell] = state.corner;
    this.center[state.cell] = state.center;
  }

  /**
   * Run `mutate`, recording whatever it changed as one undoable move.
   * Returns false if nothing actually changed, so no-op taps do not fill the
   * undo history with moves that appear to do nothing when replayed.
   */
  private transact(cells: Iterable<number>, mutate: () => void): boolean {
    const touched = [...new Set(cells)];
    const before = touched.map((cell) => this.snapshotOf(cell));
    mutate();
    const after = touched.map((cell) => this.snapshotOf(cell));

    const changed = before.some(
      (state, i) =>
        state.value !== after[i]!.value ||
        state.corner !== after[i]!.corner ||
        state.center !== after[i]!.center,
    );
    if (!changed) return false;

    this.undoStack.push({ before, after });
    this.redoStack.length = 0;
    return true;
  }

  /**
   * Place (or clear, with 0) a digit.
   *
   * Placing also clears the cell's own marks and strips the digit from its
   * peers' notes -- the busywork removal the study found players value, folded
   * into the same move so one undo puts it all back.
   */
  setValue(cell: number, digit: Digit | 0, options: { cleanPeerMarks?: boolean } = {}): boolean {
    if (this.isGiven(cell)) return false;
    const clean = options.cleanPeerMarks ?? true;
    const affected = [cell, ...(clean && digit !== 0 ? this.cs.peers[cell]! : [])];

    return this.transact(affected, () => {
      this.values[cell] = digit;
      this.corner[cell] = 0;
      this.center[cell] = 0;
      if (clean && digit !== 0) {
        const bit = maskOf(digit);
        for (const peer of this.cs.peers[cell]!) {
          this.corner[peer]! &= ~bit;
          this.center[peer]! &= ~bit;
        }
      }
    });
  }

  toggleMark(cell: number, digit: Digit, kind: MarkKind): boolean {
    if (this.isGiven(cell) || this.values[cell] !== EMPTY) return false;
    const marks = kind === "corner" ? this.corner : this.center;
    return this.transact([cell], () => {
      marks[cell]! ^= maskOf(digit);
    });
  }

  /** Clear a cell: its digit if it has one, otherwise its notes. */
  erase(cell: number): boolean {
    if (this.isGiven(cell)) return false;
    return this.transact([cell], () => {
      if (this.values[cell] !== EMPTY) this.values[cell] = EMPTY;
      else {
        this.corner[cell] = 0;
        this.center[cell] = 0;
      }
    });
  }

  /** Replace every empty cell's centre marks with the true candidates. */
  fillAutoCandidates(): boolean {
    const candidates = this.autoCandidates();
    const empty: number[] = [];
    for (let cell = 0; cell < CELLS; cell++) if (this.values[cell] === EMPTY) empty.push(cell);

    return this.transact(empty, () => {
      for (const cell of empty) this.center[cell] = candidates[cell]!;
    });
  }

  undo(): boolean {
    const move = this.undoStack.pop();
    if (!move) return false;
    for (const state of move.before) this.applySnapshot(state);
    this.redoStack.push(move);
    return true;
  }

  redo(): boolean {
    const move = this.redoStack.pop();
    if (!move) return false;
    for (const state of move.after) this.applySnapshot(state);
    this.undoStack.push(move);
    return true;
  }

  // ---------- clock ----------

  start(now = Date.now()): void {
    if (this.runningSince === null) this.runningSince = now;
  }

  pause(now = Date.now()): void {
    if (this.runningSince === null) return;
    this.banked += now - this.runningSince;
    this.runningSince = null;
  }

  get isRunning(): boolean {
    return this.runningSince !== null;
  }

  elapsedMs(now = Date.now()): number {
    return this.banked + (this.runningSince === null ? 0 : now - this.runningSince);
  }
}

export function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
